import { API_URL } from "../utils";
import { fetchWithAuth } from "../utils/api";
import { WorkoutApiService } from "./api/WorkoutApiService";

export class WorkoutService {
    static async deleteWorkoutSet(setId: number) {
        return WorkoutApiService.deleteWorkoutSet(setId);
    }

    /**
     * Adds a workout set (AMRAP or traditional)
     * @param {string} dateKey - The date key for the workout
     * @param {any} formData - The workout form data
     * @param {(msg: string) => void} setWorkoutError - Callback to set error messages
     * @returns {Promise<Response | undefined>} - The fetch response or undefined if validation fails
     */
    static async AddWorkoutSet(dateKey: string, formData: any, setWorkoutError: (msg: string) => void): Promise<Response | undefined> {
        let res;
        if (formData.workout_type === 'amrap') {
            // Handle AMRAP workout
            if (!formData.amrap_time_minutes || !formData.amrap_details || formData.amrap_details.length === 0) {
                setWorkoutError('AMRAP workout requires time and at least one exercise.');
                return;
            }
            res = await WorkoutApiService.saveAmrapWorkout(
                dateKey,
                formData.amrap_time_minutes,
                formData.comment,
                formData.amrap_details
            );
        } else {
            // Handle traditional workout
            const { exercise: exerciseInput, comment, details } = formData;
            if (!exerciseInput || !details || details.length === 0) return;
            let exerciseId: number;
            if (typeof exerciseInput === 'string' && !isNaN(parseInt(exerciseInput, 10))) {
                exerciseId = parseInt(exerciseInput, 10);
            } else if (typeof exerciseInput === 'object' && exerciseInput !== null && typeof exerciseInput.id === 'number') {
                exerciseId = exerciseInput.id;
            } else {
                setWorkoutError('Invalid exercise selected.');
                return;
            }
            res = await WorkoutApiService.addTraditionalWorkout(
                dateKey,
                exerciseId,
                comment,
                details
            );
        }
        return res;
    }

    static async getExerciseDetails(exerciseId: number) {
        const response = await fetchWithAuth(`${API_URL}/exercises/${exerciseId}/details`);
        return response.json();
    }

    static async fetchWorkoutsForDate(dateKey: string) {
        return WorkoutApiService.getWorkoutsForDate(dateKey);
    }

    /**
     * Update a traditional workout set
     * @param setId - The workout set ID
     * @param data - Updated workout data
     * @returns Response from the API
     */
    static async updateWorkoutSet(
        setId: number,
        data: {
            exerciseId?: number | null;
            comment?: string | null;
            details: Array<{
                id?: number;
                series: number;
                reps: number;
                weight: number;
                rest_time_seconds?: number | null;
                rir?: number | null;
            }>;
        }
    ): Promise<Response> {
        return WorkoutApiService.updateWorkoutSet(setId, data);
    }

    /**
     * Update an AMRAP workout
     * @param amrapTimeSeconds - The original AMRAP time in seconds
     * @param data - Updated AMRAP workout data
     * @returns Response from the API
     */
    static async updateAmrapWorkout(
        amrapTimeSeconds: number,
        data: {
            time_minutes: number;
            comment: string | null;
            amrap_details: Array<{
                exercise_id: number;
                reps: number;
            }>;
        }
    ): Promise<Response> {
        return WorkoutApiService.updateAmrapWorkout(amrapTimeSeconds, data);
    }

    /**
     * Delete a specific workout detail (set)
     * @param detailId - The workout detail ID
     * @returns Response from the API
     */
    static async deleteWorkoutDetail(detailId: number): Promise<Response> {
        return WorkoutApiService.deleteWorkoutDetail(detailId);
    }

    /**
     * Fetch all dates with workouts for a specific month
     * @param year - Year (e.g., 2026)
     * @param month - Month number (1-12)
     * @returns Array of date strings in YYYY-MM-DD format
     */
    static async fetchWorkoutDates(year: number, month: number): Promise<string[]> {
        return WorkoutApiService.getWorkoutDates(year, month);
    }
}
